<template>
  <div>
    <LibraryBanner :title="studentName">
      <template v-slot:breadcrumbList>
        <li><a :href="toolkitUrls.libraryAllFolders">Your Library</a></li>
        <li class="active">{{ studentName }}</li>
      </template>
      <template v-slot:buttons>
        <ul class="student-actions list-unstyled text-right" v-if="!loading">
          <li v-if="shareVisibility">
            <button class="share-btn btn btn-primary" @click="openShareModal"><i class="fa fa-link" aria-hidden="true"/> Share Folder</button>
          </li>
          <li>
            <button class="edit-student-button btn btn-sm btn-link text-link-darker" @click="openModal">Edit Folder</button>
          </li>
          <li>
            <button
              class="btn btn-sm delete-student-button btn-link text-danger-darker"
              @click="deleteStudent">Delete Folder</button>
          </li>
        </ul>
      </template>
    </LibraryBanner>
    <modal
      v-model="isShareModalOpen"
      title="Share with Others"
      v-if="shareVisibility"
      size="md">
      <StudentShareForm
        :student-uuid="uuid"
        :user-uuid="userId"
        :share-token.sync="shareToken" />

      <template v-slot:footer>
        <button class="btn btn-default" @click="isShareModalOpen=false">Done</button>
      </template>
    </modal>
    <modal
      v-model="isEditStudentModalOpen"
      title="Edit Folder"
      size="sm">
      <div class="form-group" :class="{ 'has-error': isStudentNameInvalid}">
        <label>Folder Name <StudentPiiTooltip/></label>
        <input
          type="text"
          class="form-control fs-block"
          :placeholder="studentName"
          name="newStudentName"
          v-model="newStudentName">
        <small v-if="studentHasConflictingName" class="help-block no-margin-bottom text-danger">You cannot have two folders with the same name.</small>
      </div>
      <div slot="footer">
        <span v-if="!editingStudent">
          <button class="btn btn-default" @click="isEditStudentModalOpen=false">Cancel</button>
          <button
            class="btn btn-primary"
            @click="editStudent(newStudentName)"
            :disabled="isStudentNameInvalid || !newNameIsDifferent">Save</button>
        </span>
        <span v-if="editingStudent" class="text-muted">
          <i class="fa fa-circle-o-notch fa-spin"/>&nbsp;Editing Folder...
        </span>
      </div>
    </modal>

    <div class="container">
      <div v-if="loading">
        <p class="gb-text-2 text-muted"><i class="fa fa-circle-o-notch fa-spin"/>&nbsp;Loading resources...</p>
      </div>
      <template v-if="!loading">
        <div class="row" v-if="resources.length > 0">
          <div class="col-sm-8">
            <ResourcesLibrary :resources="resources" />
          </div>
        </div>

        <div class="row">
          <div class="col-md-4">
            <PresentLevelsLibrary context="Present Levels" :presentlevels="presentlevels"/>
          </div>
          <div class="col-md-4">
            <GoalsLibrary context="Student Goals" :goals="goals"/>
          </div>
          <div class="col-md-4">
            <StrategyLibrary context="Student Strategies" :strategies="strategies"/>
          </div>
        </div>
      </template>
      <SubmitFeedback type="Library" />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import { setTitle } from '@/util/title'
import { deleteStudent, updateStudent } from '@/api/library'
import LibraryBanner from '@/components/library/LibraryBanner'
import StudentShareForm from '@/components/library/StudentShareForm'
import GoalsLibrary from '@/components/library/GoalsLibrary'
import SubmitFeedback from '@/components/SubmitFeedback'
import StrategyLibrary from '@/components/library/StrategyLibrary'
import PresentLevelsLibrary from '@/components/library/PresentLevelsLibrary'
import ResourcesLibrary from '@/components/library/ResourcesLibrary'
import StudentPiiTooltip from '@/components/library/StudentPiiTooltip'
import router from '@/router'
import { toolkitUrls } from '@/links'

export default {
  name: 'StudentLibraryPage',
  components: {
    LibraryBanner,
    StudentShareForm,
    GoalsLibrary,
    StrategyLibrary,
    PresentLevelsLibrary,
    ResourcesLibrary,
    StudentPiiTooltip,
    SubmitFeedback
  },
  props: {
    uuid: {
      type: String,
      required: true
    }
  },
  data: function() {
    return {
      studentName: '',
      isEditStudentModalOpen: false,
      isShareModalOpen: false,
      shareToken: '',
      newStudentName: '',
      editingStudent: false,
      toolkitUrls
    }
  },
  computed: {
    ...mapState({
      loading: state => state.library.loadingResources,
      userId: state => state.user.user.uuid,
      goals: state => state.library.goals,
      strategies: state => state.library.strategies,
      presentlevels: state => state.library.presentlevels,
      resources: state => state.library.resources,
      student: state => state.library.currentStudent,
      students: state => state.library.students
    }),
    ...mapGetters('library', ['currentStudentContentCount']),
    ...mapGetters('visibility', ['shareVisibility']),
    newNameIsDifferent() {
      return this.studentName !== this.newStudentName
    },
    studentHasConflictingName() {
      let result = false
      this.students.filter(student => student.name !== this.studentName).forEach(student => {
        if (student.name === this.newStudentName) {
          result = true
        }
      })
      return result
    },
    isStudentNameInvalid() {
      return this.studentHasConflictingName || this.newStudentName.length === 0
    }
  },
  methods: {
    ...mapMutations('library', ['resetFilters']),
    async fetchResources() {
      let resp = await this.$store.dispatch('library/fetchResourcesForStudent', {
        studentId: this.uuid,
      })
      this.shareToken = resp.collection.share_token

      const {ref, position, updatedAt} = this.$route.query
      this.$sendEvent('Viewed Student Library Page', {
        ref,
        position,
        updatedAt,
      })
    },
    deleteStudent() {
      if (
        confirm(
          'Are you sure you want to delete this folder? You will lose all resources associated with the folder and any shared links will become invalid. Others will not be able to access the folder unless they have copied the folder to their Library.'
        )
      ) {
        deleteStudent(this.student.uuid, this.userId).then(() => {
          this.$store.dispatch('library/fetchStudents', this.userId)
          this.$sendEvent('Deleted Student', {})
          window.location.href = toolkitUrls.libraryAllFolders
        })
      }
      this.$sendEvent('Clicked Delete Student Button', {})
    },
    async editStudent(newName) {
      this.editingStudent = true
      if (this.studentHasConflictingName) {
        this.editingStudent = false
        return
      }
      await updateStudent({
        userUuid: this.userId,
        studentUuid: this.uuid,
        updateParams: {
          name: newName
        }
      })
      this.$sendEvent('Edited Student')
      this.editingStudent = false
      this.studentName = newName
      this.isEditStudentModalOpen = false
    },
    fetchStudents() {
      if (this.userId && this.userId.length > 0) {
        this.$store.dispatch('library/fetchStudents', this.userId)
      }
    },
    openModal() {
      this.isEditStudentModalOpen = true
      this.$sendEvent('Clicked Edit Student Button', {})
    },
    openShareModal() {
      this.isShareModalOpen = true
      this.$sendEvent('Clicked Share Student')
    }
  },
  created() {
    this.resetFilters()
    this.fetchResources()
    this.fetchStudents() // need to have students to make sure they don't rename student same as another
  },
  watch: {
    student() {
      if (this.student.creator !== this.userId) {
        // student belongs to different user, so back to library page
        window.location.href = toolkitUrls.libraryAllFolders
        return
      }
      setTitle(`${this.student.name} - Library`)
      this.studentName = this.student.name
      this.newStudentName = this.student.name
    }
  },
  eventContext() {
    return {
      student_uuid: this.uuid,
      student_content_count: this.currentStudentContentCount,
    }
  }
}
</script>

<style scoped lang="less">
@import '~@/mixins.less';
.student-actions {
  margin-bottom: 0;
  .share-btn {
    margin-bottom: 4px;
  }
  .btn-sm.btn-link {
    padding: 0;
  }
}
</style>
